import { Box, Chip } from '@mui/material';
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import * as auditApi from '@/api/audit';
import type { AuditLogEntry } from '@/api/audit';
import { ResourceDataGrid, type ResourceDataGridParams } from '@/components/DataGrid/ResourceDataGrid';
import { ValueActions, utcTitle } from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';

import {
  auditActorActions,
  auditEventActions,
  auditIpActions,
  auditRowActions,
  type AuditCtx,
} from '../userLinks';

const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');
const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');

interface Props {
  userId: number;
  /** The `target_email` of every row here — what the audit grid filters on. */
  userEmail: string;
  /** `console_audit_view`: the /audit page refuses without it, so the links
   *  to it are only offered to viewers who can follow them. */
  canViewAudit: boolean;
}

export function AuditTab({ userId, userEmail, canViewAudit }: Props) {
  const navigate = useNavigate();
  const ctx: AuditCtx = { userEmail, canViewAudit, canOpenUsers: true };

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(
    () => [
      {
        field: 'created_ts',
        headerName: 'When',
        width: 200,
        filterable: false,
        // The timestamp cell carries the row-level references the grid has no
        // column for: the entry itself, its tenant, the background task that
        // wrote it and the record it changed.
        renderCell: (p) => (
          <ValueActions actions={auditRowActions(p.row, ctx)} title={utcTitle(p.row.created_ts)}>
            {formatDate(p.row.created_ts)}
          </ValueActions>
        ),
      },
      {
        field: 'event',
        headerName: 'Event',
        flex: 1.2,
        filterOperators: containsOnly,
        renderCell: (p) => (
          <ValueActions actions={auditEventActions(p.row.event, ctx)}>{p.row.event}</ValueActions>
        ),
      },
      {
        field: 'success',
        headerName: 'Success',
        width: 110,
        type: 'singleSelect',
        valueOptions: [
          { value: 'true', label: 'OK' },
          { value: 'false', label: 'Fail' },
        ],
        filterOperators: isOperator,
        renderCell: (p) =>
          p.row.success ? (
            <Chip size="small" color="success" label="OK" />
          ) : (
            <Chip size="small" color="warning" label="Fail" />
          ),
      },
      {
        field: 'actor',
        headerName: 'Actor',
        flex: 1,
        filterable: false,
        renderCell: (p) => {
          const a = p.row.actor;
          if (!a) return '—';
          return <ValueActions actions={auditActorActions(a, ctx)}>{a.email}</ValueActions>;
        },
      },
      {
        field: 'ip',
        headerName: 'IP',
        width: 140,
        filterOperators: containsOnly,
        renderCell: (p) =>
          p.row.ip ? (
            <ValueActions actions={auditIpActions(p.row.ip, ctx)}>{p.row.ip}</ValueActions>
          ) : (
            '—'
          ),
      },
    ],
    // `ctx` is rebuilt on every render from two primitives — depend on those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userEmail, canViewAudit],
  );

  // One filter set for the page AND its on-demand total, so the total always
  // describes the rows shown (the grid count-mode rule).
  function listFilters(params: ResourceDataGridParams) {
    const extra = params.extra ?? {};
    return {
      target_user: userId,
      ordering: params.ordering,
      search: params.search,
      ...(extra.event_contains ? { event_contains: String(extra.event_contains) } : {}),
      ...(extra.ip ? { ip: String(extra.ip) } : {}),
      ...(extra.success !== undefined ? { success: extra.success === 'true' } : {}),
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
    };
  }

  function queryFn(params: ResourceDataGridParams) {
    return auditApi.list({
      ...listFilters(params),
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ...(params.countMode ? { count_mode: params.countMode } : {}),
    });
  }

  const countOnly = (params: ResourceDataGridParams) => auditApi.count(listFilters(params));

  return (
    <Box>
      <ResourceDataGrid<AuditLogEntry>
        queryKey={['audit', 'byTarget', userId]}
        queryFn={queryFn}
        // The audit endpoint defers its row total (the grid count-mode rule).
        // Without the count layer the pager read the -1 sentinel as "1–25 of
        // more than 25" over two rows; with it, a short last page is its own
        // exact total and longer histories get "Show total". Shares the /audit
        // page's mode preference.
        countMode={{ grid: 'audit', countOnly }}
        allowExtendedTimeout
        columns={columns}
        layout="auto"
        defaultPageSize={25}
        defaultSort={{ field: 'created_ts', sort: 'desc' }}
        filterFieldMap={{ event: 'event_contains', ip: 'ip', success: 'success' }}
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Event, email or IP',
          exportFileName: 'user-audit',
        }}
        // The /audit route is permission-gated, so only viewers who can read
        // it get the row click (a click that lands on "no access" is worse
        // than an inert row).
        {...(canViewAudit
          ? {
              sx: { '& .MuiDataGrid-row': { cursor: 'pointer' } },
              // Same tab, like every other row click in the console.
              onRowClick: (id: number) => navigate(`/audit/${id}`),
            }
          : {})}
      />
    </Box>
  );
}
